import React, { useCallback, useEffect } from 'react';
import { StyleSheet, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { BottomTabBarProps } from '@react-navigation/bottom-tabs';

import { FeatureHighlightTooltip, TOOLTIP_WIDTH } from '@/components/FeatureHighlightTooltip';
import { BOTTOM_TABS_HEIGHT } from '@/components/TabBar';
import { useGlobalState } from '@/hooks/use-global-state';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useShowOneTime } from '@/hooks/use-show-one-time';

const TAB_BAR_PADDING_H = 8;

type FeedTabTooltipProps = Pick<BottomTabBarProps, 'state' | 'navigation'>;

export const FeedTabTooltip = ({ state, navigation }: FeedTabTooltipProps) => {
    const insets = useSafeAreaInsets();
    const socialFeedPrismicFlag = useJurisdictionStore(
        store => store.jurisdictionSettings?.productConfig?.settings?.social_feed_enabled
    );
    const socialFeedAPIEnabled = useGlobalState(globalState => globalState.entryFeedConfig?.socialFeedEnabled);
    const socialFeedEnabled = socialFeedPrismicFlag && socialFeedAPIEnabled;

    const { show: showFeedTooltip, handleInteraction: dismissFeedTooltip } = useShowOneTime(
        'Pickem_FeedTab_FeatureTooltip'
    );
    const { width: screenWidth } = useWindowDimensions();

    const feedTabIndex = state.routes.findIndex(route => route.name === 'Feed');
    const lobbyTabIndex = state.routes.findIndex(route => route.name === 'Lobby');
    const isFeedTabActive = state.index === feedTabIndex;
    const isLobbyActive = state.index === lobbyTabIndex;

    // Check if user is on the root LobbyScreen (not on Promos/FeaturedLineups)
    const lobbyRoute = state.routes[lobbyTabIndex];
    const lobbyState = lobbyRoute?.state;
    const isOnLobbyScreen = !lobbyState || lobbyState.routes[lobbyState.index ?? 0]?.name === 'LobbyScreen';

    const isFeedTabAvailable = socialFeedEnabled && feedTabIndex !== -1;
    const isOnLobbyRoot = isLobbyActive && isOnLobbyScreen;
    const showTooltip = isFeedTabAvailable && showFeedTooltip && isOnLobbyRoot;

    // Permanently dismiss when navigating to the Feed tab
    useEffect(() => {
        if (isFeedTabActive && showFeedTooltip) {
            dismissFeedTooltip();
        }
    }, [isFeedTabActive, showFeedTooltip, dismissFeedTooltip]);

    const handleExplore = useCallback(async () => {
        await dismissFeedTooltip();
        navigation.navigate('Feed');
    }, [dismissFeedTooltip, navigation]);

    if (!showTooltip) {
        return null;
    }

    const totalTabs = state.routes.length;
    const tabWidth = (screenWidth - TAB_BAR_PADDING_H * 2) / totalTabs;
    const feedTabCenterX = TAB_BAR_PADDING_H + feedTabIndex * tabWidth + tabWidth / 2;
    const tooltipLeft = feedTabCenterX - TOOLTIP_WIDTH / 2;

    const tooltipBottom = insets.bottom + BOTTOM_TABS_HEIGHT - 4;

    return (
        <View
            pointerEvents="box-none"
            style={[feedTabTooltipStyles.container, { left: tooltipLeft, bottom: tooltipBottom }]}
        >
            <FeatureHighlightTooltip
                title="Social Feed"
                description="Browse and tail others from the Betr community."
                onDismiss={dismissFeedTooltip}
                onExplore={handleExplore}
            />
        </View>
    );
};

const feedTabTooltipStyles = StyleSheet.create({
    container: {
        position: 'absolute',
        zIndex: 10,
    },
});
